import React from "react";
import { AlertTriangle, Check, MapPin } from "lucide-react";

import { alertAction, alertTone, dwellGap, groupAlertsByHour } from "../utils/geotabLiveAlerts.js";
import { chipClass, fmtDelta, fmtDuration, fmtTime } from "./geotab/geotabFormat.js";

const FILTERS = [["all", "Tout"], ["arrival", "Arrivées"], ["departure", "Terminés"], ["off", "Hors plan"]];
const TONE_CLASS = { off: "bg-[#c2410c] text-white", done: "bg-[#15803d] text-white", loading: "bg-[#3f5585] text-white", delivery: "bg-[#d3c195] text-[#373018]" };
const dayLabel = (date) => (date ? new Date(`${date}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "long" }) : "");

// Onglet de la cloche : tous les avis de la journée, groupés par heure. L'ouvrir les marque lus.
export default function GeotabLiveAlertsJournal({ live, onOpen }) {
  const [filter, setFilter] = React.useState("all");
  const [highlighted] = React.useState(() => new Set(live.unreadIds));
  React.useEffect(() => {
    live.unreadIds.forEach((id) => highlighted.add(id));
    live.markAllRead();
  }, [live.unreadIds, live.markAllRead, highlighted]);
  const events = filter === "all" ? live.events
    : live.events.filter((event) => (filter === "off" ? event.site_kind === "off" : event.type === filter));
  return (
    <>
      <div className="flex gap-1.5 border-b border-slate-100 px-4 py-2">
        {FILTERS.map(([key, label]) => (
          <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)}
            className={`inline-flex h-6 items-center rounded-[4px] border px-2.5 text-[11px] font-extrabold ${filter === key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="min-h-24 overflow-x-hidden overflow-y-auto" data-geotab-alert-journal="true">
        {live.error && !live.events.length ? <p className="m-0 px-4 py-6 text-sm text-red-700">{live.error}</p> : null}
        {!live.error && !events.length ? (
          <p className="m-0 px-5 py-8 text-center text-xs leading-5 text-slate-500">Aucun avis pour l'instant aujourd'hui.</p>
        ) : null}
        {groupAlertsByHour(events).map((group) => (
          <section key={group.hour} aria-label={`${group.hour} h`}>
            <div className="border-b border-slate-100 bg-slate-50 px-4 pb-1 pt-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">{group.hour} h</div>
            {group.events.map((event) => {
              const gap = dwellGap(event, live.tolerance);
              return (
                <button key={event.id} type="button" onClick={() => onOpen?.(event)} data-geotab-alert-row={event.id}
                  className={`grid w-full grid-cols-[2.4rem_1.25rem_minmax(0,1fr)] items-start gap-2.5 border-b border-slate-100 px-4 py-2 text-left transition hover:bg-slate-50 ${highlighted.has(event.id) ? "shadow-[inset_3px_0_0_#3f5585]" : ""}`}>
                  <span className="pt-px text-xs font-extrabold tabular-nums text-slate-900">{fmtTime(event.at)}</span>
                  <span aria-hidden="true" className={`grid h-5 w-5 place-items-center rounded-[4px] ${TONE_CLASS[alertTone(event)]}`} data-geotab-alert-tone={alertTone(event)}>
                    {event.type === "departure" ? <Check className="h-3 w-3" /> : event.site_kind === "off" ? <AlertTriangle className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[12.5px] font-bold leading-5 text-slate-900">{[event.driver, event.truck].filter(Boolean).join(" · ")}</span>
                    <span className="block text-[11.5px] leading-5 text-slate-500">
                      {alertAction(event)} {event.site}
                      {event.type === "departure" ? ` · ${fmtDuration(event.dwell)}` : event.site_kind === "off" ? ` · arrêté ${fmtDuration(event.elapsed)}` : ""}
                      {gap ? <span className={`ml-1.5 inline-flex h-4 items-center rounded-[3px] px-1 text-[10px] font-extrabold tabular-nums ${chipClass(gap.tone)}`}>{fmtDelta(gap.gap)}</span> : null}
                    </span>
                  </span>
                </button>
              );
            })}
          </section>
        ))}
      </div>
      <footer className="flex items-center gap-2 bg-slate-50 px-4 py-2 text-[11px] text-slate-500">
        Journée du {dayLabel(live.date)} · {live.events.length} avis
      </footer>
    </>
  );
}
